import { useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Image,
  Input,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tr,
  useToast
} from '@chakra-ui/react'

import Modal from '../../modal'
import { useRanContext } from '../../ran-state-provider'

import {
  getUserAccountInfo,
  convertReferralPointsToEPoints,
  forgotValuablePin,
  changeValuablePin
} from '../../../lib/api-helper'

import {
  getClassImage,
  getSchoolSmallLogo
} from '../../../lib/ran-helper'

const View = ({ onClose }) => {
  const toast = useToast()
  const { setUserBasicInfo, setAuthentication } = useRanContext()

  const [isLoading, setLoading] = useState(true)
  const [data, setData] = useState({})

  const [selectedCharacter, setSelectedCharacter] = useState(null)

  const [openConvertPinModal, setOpenConvertPinModal] = useState(false)
  const [openForgotValuablePinModal, setOpenForgotValuablePinModal] = useState(false)
  const [openChangeValuablePinModal, setOpenChangeValuablePinModal] = useState(false)

  const [pinCode, setPinCode] = useState('')

  const [changePinForm, setChangePinForm] = useState({
    currentValuablePin: '',
    currentPassword: '',
    newValuablePin: '',
    confirmValuablePin: ''
  })

  const [isConverting, setIsConverting] = useState(false)
  const [isSendingForgotPin, setIsSendingForgotPin] = useState(false)
  const [isChangingPin, setIsChangingPin] = useState(false)

  useEffect(() => {
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    setLoading(true)

    const result = await getUserAccountInfo(setAuthentication)

    if (result?.status === 200) {
      const normalized = {
        ...result.data,
        referralPoints:
          result.data.referralPoints ??
          result.data.ReferralPoints,
        characters:
          result.data.characters ??
          result.data.Characters ??
          []
      }

      setUserBasicInfo(normalized)
      setData(normalized)
    }

    setLoading(false)
  }

  const getCharacterNumber = character => {
    return (
      character?.chaNum ??
      character?.ChaNum ??
      character?.characterNumber ??
      character?.CharacterNumber ??
      character?.characterId ??
      character?.CharacterId ??
      character?.id ??
      character?.Id ??
      0
    )
  }

  const getCharacterName = character => {
    return (
      character?.name ??
      character?.Name ??
      character?.chaName ??
      character?.ChaName ??
      ''
    )
  }

  const getCharacterClassId = character => {
    return (
      character?.classId ??
      character?.ClassId ??
      character?.chaClass ??
      character?.ChaClass ??
      0
    )
  }

  const getCharacterClass = character => {
    return (
      character?.class ??
      character?.Class ??
      ''
    )
  }

  const getCharacterSchool = character => {
    return (
      character?.school ??
      character?.School ??
      0
    )
  }

  const getCharacterLevel = character => {
    return (
      character?.level ??
      character?.Level ??
      character?.chaLevel ??
      character?.ChaLevel ??
      0
    )
  }

  const getCharacterGold = character => {
    return (
      character?.gold ??
      character?.Gold ??
      character?.chaMoney ??
      character?.ChaMoney ??
      0
    )
  }

  const getCharacterStatus = character => {
    return (
      character?.status ??
      character?.Status ??
      'active'
    )
  }
  

  const closeForgotValuablePinModal = () => {
    setOpenForgotValuablePinModal(false)
    setSelectedCharacter(null)
  }

  const closeChangeValuablePinModal = () => {
    setOpenChangeValuablePinModal(false)
    setSelectedCharacter(null)
    resetChangePinForm()
  }

  const handleConvert = async () => {
    if (!pinCode) return

    setIsConverting(true)

    const result = await convertReferralPointsToEPoints(
      pinCode,
      setAuthentication
    )

    setIsConverting(false)

    if (!result) return

    if (result.success) {
      toast({
        title: 'Conversion Successful',
        description: `Converted ${data.referralPoints} Referral Points to E-Points.`,
        status: 'success',
        duration: 3000
      })

      setData(prev => ({ ...prev, referralPoints: 0 }))
      setOpenConvertPinModal(false)
      setPinCode('')
    } else {
      toast({
        title: 'Conversion Failed',
        description:
          result.errors?.[0] ||
          result.message ||
          'Unable to convert Referral Points.',
        status: 'error',
        duration: 3000
      })
    }
  }

  const handleForgotValuablePin = async () => {
    const chaNum = getCharacterNumber(selectedCharacter)
    const chaName = getCharacterName(selectedCharacter)

    if (!chaNum && !chaName) {
      toast({
        title: 'Invalid Character',
        description: 'Character information was not found.',
        status: 'error',
        duration: 3000
      })

      return
    }

    setIsSendingForgotPin(true)

    const result = await forgotValuablePin(
      {
        ChaNum: chaNum,
        ChaName: chaName
      },
      setAuthentication
    )

    setIsSendingForgotPin(false)

    if (!result) return

    if (result.success) {
      toast({
        title: 'Valuable PIN Sent',
        description:
          result.message ||
          result.data ||
          `Your Valuable PIN has been sent to ${data.email}.`,
        status: 'success',
        duration: 3000
      })

      closeForgotValuablePinModal()
    } else {
      toast({
        title: 'Request Failed',
        description:
          result.errors?.[0] ||
          result.message ||
          'Unable to send Valuable PIN to your email.',
        status: 'error',
        duration: 3000
      })
    }
  }

const handleChangePinInput = e => {
  const { name, value } = e.target

  const pinFields = [
    'currentValuablePin',
    'newValuablePin',
    'confirmValuablePin'
  ]

  if (pinFields.includes(name)) {
    const numbersOnly = value.replace(/\D/g, '').slice(0, 6)

    setChangePinForm(prev => ({
      ...prev,
      [name]: numbersOnly
    }))

    return
  }

  setChangePinForm(prev => ({
    ...prev,
    [name]: value
  }))
}

  const resetChangePinForm = () => {
    setChangePinForm({
      currentValuablePin: '',
      currentPassword: '',
      newValuablePin: '',
      confirmValuablePin: ''
    })
  }
const isWeakValuablePin = pin => {
  if (!/^\d{6}$/.test(pin)) return true

  // Same digit: 111111, 222222, 000000
  if (/^(\d)\1{5}$/.test(pin)) return true

  // Repeated 2-digit pattern: 121212, 343434
  if (pin.slice(0, 2).repeat(3) === pin) return true

  // Repeated 3-digit pattern: 123123, 456456
  if (pin.slice(0, 3).repeat(2) === pin) return true

  // Ascending sequence: 123456, 234567, 012345
  let ascending = true
  for (let i = 1; i < pin.length; i++) {
    if (Number(pin[i]) !== Number(pin[i - 1]) + 1) {
      ascending = false
      break
    }
  }

  if (ascending) return true

  // Descending sequence: 654321, 987654
  let descending = true
  for (let i = 1; i < pin.length; i++) {
    if (Number(pin[i]) !== Number(pin[i - 1]) - 1) {
      descending = false
      break
    }
  }

  if (descending) return true

  return false
}
  const handleChangeValuablePin = async () => {
    const chaNum = getCharacterNumber(selectedCharacter)
    const chaName = getCharacterName(selectedCharacter)

    const {
      currentValuablePin,
      currentPassword,
      newValuablePin,
      confirmValuablePin
    } = changePinForm

    if (
  currentValuablePin.length !== 6 ||
  newValuablePin.length !== 6 ||
  confirmValuablePin.length !== 6
) {
  toast({
    title: 'Invalid PIN Length',
    description: 'Valuable PIN must be exactly 6 digits.',
    status: 'error',
    duration: 3000
  })

  return
}



    if (!chaNum && !chaName) {
      toast({
        title: 'Invalid Character',
        description: 'Character information was not found.',
        status: 'error',
        duration: 3000
      })

      return
    }

    if (
      !currentValuablePin ||
      !currentPassword ||
      !newValuablePin ||
      !confirmValuablePin
    ) {
      toast({
        title: 'Incomplete Details',
        description: 'Please complete all required fields.',
        status: 'warning',
        duration: 3000
      })

      return
    }

    if (newValuablePin !== confirmValuablePin) {
      toast({
        title: 'PIN Mismatch',
        description: 'New Valuable PIN and Confirm Valuable PIN do not match.',
        status: 'error',
        duration: 3000
      })

      return
    }

    if (isWeakValuablePin(newValuablePin)) {
  toast({
    title: 'Weak PIN Not Allowed',
    description: 'Please use a stronger 6-digit Valuable PIN.',
    status: 'error',
    duration: 3000
  })

  return
}

    setIsChangingPin(true)

    const result = await changeValuablePin(
      {
        ChaNum: chaNum,
        ChaName: chaName,
        CurrentValuablePin: currentValuablePin,
        CurrentPassword: currentPassword,
        NewValuablePin: newValuablePin,
        ConfirmValuablePin: confirmValuablePin
      },
      setAuthentication
    )

    setIsChangingPin(false)

    if (!result) return

    if (result.success) {
      toast({
        title: 'Valuable PIN Changed',
        description:
          result.message ||
          result.data ||
          'Your Valuable PIN has been changed successfully.',
        status: 'success',
        duration: 3000
      })

      closeChangeValuablePinModal()
    } else {
      toast({
        title: 'Change PIN Failed',
        description:
          result.errors?.[0] ||
          result.message ||
          'Unable to change Valuable PIN.',
        status: 'error',
        duration: 3000
      })
    }
  }

const isChangePinDisabled =
  changePinForm.currentValuablePin.length !== 6 ||
  !changePinForm.currentPassword ||
  changePinForm.newValuablePin.length !== 6 ||
  changePinForm.confirmValuablePin.length !== 6 ||
  changePinForm.newValuablePin !== changePinForm.confirmValuablePin ||
  isWeakValuablePin(changePinForm.newValuablePin) ||
  isChangingPin

  return (
    <>
      <Modal header="ACCOUNT INFO" isOpen onClose={onClose} fitContent>
        {isLoading ? (
          <Center>
            <Spinner />
          </Center>
        ) : (
          <>
            <TableContainer>
              <Table size="sm">
                <Tbody>
                  <Tr>
                    <Td>Username</Td>
                    <Td>
                      <b>{data.username}</b>
                    </Td>
                  </Tr>

                  <Tr>
                    <Td>Email address</Td>
                    <Td>
                      <b>{data.email}</b>
                    </Td>
                  </Tr>

                  <Tr>
                    <Td>Status</Td>
                    <Td>
                      <b>{data.status}</b>
                    </Td>
                  </Tr>

                  <Tr>
                    <Td>Last Login Date</Td>
                    <Td>
                      <b>{data.lastLoginDate}</b>
                    </Td>
                  </Tr>

                  <Tr>
                    <Td>Last Used IP Address</Td>
                    <Td>
                      <b>{data.lastUsedIPAddress}</b>
                    </Td>
                  </Tr>

                  <Tr>
                    <Td>Referral Points</Td>
                    <Td>
                      <Flex align="center" gap={2}>
                        <b>{data.referralPoints || 0}</b>

                        <Button
                          size="xs"
                          colorScheme="blue"
                          isDisabled={Number(data.referralPoints) <= 0}
                          onClick={() => setOpenConvertPinModal(true)}
                        >
                          Convert to E-Points
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>

            <Text w="full" pl={4} py={1.5}>
              Characters on RAN Online PH:
            </Text>

            <Stack w="full" px={4} columns={1} spacing={2}>
              {(data.characters || []).map((character, index) => (
                <Flex key={`my-characters-${index + 1}`} align="center">
                  <Box boxSize={50} mr={1}>
                    <Image
                      src={getClassImage(getCharacterClassId(character))}
                      alt={getCharacterClass(character)}
                    />
                  </Box>

                  <Box flex={1} fontSize={12}>
                    <Flex align="center" mb={-0.5}>
                      <Image
                        h="10px"
                        src={getSchoolSmallLogo(getCharacterSchool(character))}
                        alt="school"
                        mr={2}
                      />

                      <Text flex={1} fontSize={14} fontWeight={700}>
                        {getCharacterName(character)}
                      </Text>

                      <Badge>{index + 1}</Badge>
                    </Flex>

                    <p>
                      Level: <b>{getCharacterLevel(character)}</b>
                      {' | '}
                      Gold:{' '}
                      <span
                        style={{
                          color: 'var(--chakra-colors-gold)',
                          fontWeight: 700
                        }}
                      >
                        {getCharacterGold(character)}
                      </span>
                      {' | '}
                      Status:{' '}
                      <span
                        style={{
                          color: `var(--chakra-colors-main-${
                            getCharacterStatus(character) === 'deleted'
                              ? 'red'
                              : 'green'
                          })`,
                          fontWeight: 700
                        }}
                      >
                        {getCharacterStatus(character)}
                      </span>
                    </p>

  {/* {String(getCharacterStatus(character)).toLowerCase() !== 'deleted' && (
  <Flex gap={2} mt={2}>
    <Button
      size="xs"
      colorScheme="blue"
      onClick={() => {
        setSelectedCharacter(character)
        setOpenChangeValuablePinModal(true)
      }}
    >
      Change Valuable PIN
    </Button>

    <Button
      size="xs"
      colorScheme="orange"
      onClick={() => {
        setSelectedCharacter(character)
        setOpenForgotValuablePinModal(true)
      }}
    >
      Forgot Valuable PIN
    </Button>
  </Flex>
)
} */}
                  </Box>
                </Flex>
              ))}
            </Stack>
          </>
        )}
      </Modal>

      {/* CONVERT PIN MODAL */}
      <Modal
        header="Confirm Conversion"
        isOpen={openConvertPinModal}
        onClose={() => {
          setOpenConvertPinModal(false)
          setPinCode('')
        }}
        fitContent
      >
        <Stack spacing={3}>
          <Text fontSize="sm">
            Enter your PIN code to convert Referral Points.
          </Text>

          <Input
            type="password"
            placeholder="PIN Code"
            value={pinCode}
            onChange={e => setPinCode(e.target.value)}
          />

          <Button
            colorScheme="green"
            onClick={handleConvert}
            isDisabled={!pinCode || isConverting}
            isLoading={isConverting}
          >
            Confirm
          </Button>
        </Stack>
      </Modal>

      {/* FORGOT VALUABLE PIN MODAL */}
      <Modal
        header="Forgot Valuable PIN"
        isOpen={openForgotValuablePinModal}
        onClose={closeForgotValuablePinModal}
        fitContent
      >
        <Stack spacing={3}>
          <Text fontSize="sm">
            Your Valuable PIN for this character will be sent to your registered email address.
          </Text>

          <Text fontSize="sm">
            Character: <b>{getCharacterName(selectedCharacter)}</b>
          </Text>

          <Text fontSize="sm" fontWeight={700}>
            {data.email}
          </Text>

          <Button
            colorScheme="orange"
            onClick={handleForgotValuablePin}
            isLoading={isSendingForgotPin}
            isDisabled={isSendingForgotPin}
          >
            Send Valuable PIN to Email
          </Button>
        </Stack>
      </Modal>

{/* CHANGE VALUABLE PIN MODAL */}
<Modal
  header="Change Valuable PIN"
  isOpen={openChangeValuablePinModal}
  onClose={closeChangeValuablePinModal}
  fitContent
>
  <Stack spacing={3} w="300px" maxW="90vw">
    <Text fontSize="sm">
      Character: <b>{getCharacterName(selectedCharacter)}</b>
    </Text>

    <Box>
      <Text fontSize="xs" mb={1} fontWeight={600}>
        Current Valuable PIN
      </Text>
      <Input
        type="password"
        name="currentValuablePin"
        placeholder="Enter 6-digit current PIN"
        value={changePinForm.currentValuablePin}
        onChange={handleChangePinInput}
        maxLength={6}
        inputMode="numeric"
        pattern="[0-9]*"
        bg="white"
        color="black"
        border="1px solid"
        borderColor="gray.400"
        w="100%"
        _placeholder={{ color: 'gray.500' }}
        _focus={{
          borderColor: 'blue.400',
          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)'
        }}
      />
    </Box>

    <Box>
      <Text fontSize="xs" mb={1} fontWeight={600}>
        Current Password
      </Text>
      <Input
        type="password"
        name="currentPassword"
        placeholder="Enter password"
        value={changePinForm.currentPassword}
        onChange={handleChangePinInput}
        bg="white"
        color="black"
        border="1px solid"
        borderColor="gray.400"
        w="100%"
        _placeholder={{ color: 'gray.500' }}
        _focus={{
          borderColor: 'blue.400',
          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)'
        }}
      />
    </Box>

    <Box>
      <Text fontSize="xs" mb={1} fontWeight={600}>
        {changePinForm.newValuablePin.length === 6 &&
  isWeakValuablePin(changePinForm.newValuablePin) && (
    <Text fontSize="xs" color="red.500" mt={1}>
      Weak PIN is not allowed.
    </Text>
  )}
        New Valuable PIN
      </Text>
      <Input
        type="password"
        name="newValuablePin"
        placeholder="Enter 6-digit new PIN"
        value={changePinForm.newValuablePin}
        onChange={handleChangePinInput}
        maxLength={6}
        inputMode="numeric"
        pattern="[0-9]*"
        bg="white"
        color="black"
        border="1px solid"
        borderColor="gray.400"
        w="100%"
        _placeholder={{ color: 'gray.500' }}
        _focus={{
          borderColor: 'blue.400',
          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)'
        }}
      />
    </Box>

    <Box>
      <Text fontSize="xs" mb={1} fontWeight={600}>
        Confirm Valuable PIN
      </Text>
      <Input
        type="password"
        name="confirmValuablePin"
        placeholder="Confirm 6-digit new PIN"
        value={changePinForm.confirmValuablePin}
        onChange={handleChangePinInput}
        maxLength={6}
        inputMode="numeric"
        pattern="[0-9]*"
        bg="white"
        color="black"
        border="1px solid"
        borderColor="gray.400"
        w="100%"
        _placeholder={{ color: 'gray.500' }}
        _focus={{
          borderColor: 'blue.400',
          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)'
        }}
      />
    </Box>

    <Button
      colorScheme="green"
      onClick={handleChangeValuablePin}
      isDisabled={isChangePinDisabled}
      isLoading={isChangingPin}
      w="100%"
    >
      Change Valuable PIN
    </Button>
  </Stack>
</Modal>
    </>
  )
}

export default View